import PropTypes from 'prop-types';
import exact from 'prop-types-exact';
import React, { Component } from 'react';

import { UK_DIAL_CODE } from '../../../config/helpers';
import REGEX from '../../../config/regex';
import { getErrorMessageKey, getVynePaymentErrorMessageKey } from '../../../util/errorCodes';
import jsonPost from '../../../util/jsonPost';
import { makePaymentDetailsLabelsPropTypes, makePaymentDetailsLoadPropTypes } from './PropTypes';
import { getCurrencies, getExchangeRates, getInitialCurrencyState } from '../../../util/currency';

import Button from '../Atoms/Button';
import Input from '../Atoms/Input';
import MemberAddress from '../BookingFieldset/MemberAddress';
import MemberDetails from '../BookingFieldset/MemberDetails';
import {
  initFieldsetProps,
  markRequiredFields,
  preventKeyCode,
  updateField,
  validateFieldset,
} from '../BookingFieldset/helpers';
import PaymentOptions from '../BookingSummary/PaymentOptions';
import ErrorBoundary from '../Core/ErrorBoundary';
import ErrorMessage from '../Core/ErrorMessage';
import { blockInvalidCharactersForNumberInput } from '../../../util/validation';
import CUSTOM_EVENT from '../../../util/customEvents';

const propTypes = exact({
  apiURL: PropTypes.string.isRequired,
  data: makePaymentDetailsLoadPropTypes.isRequired,
  labels: makePaymentDetailsLabelsPropTypes,
});

const defaultProps = {
  labels: undefined,
};

const requiredFields = [
  'address.addressLine1',
  'address.city',
  'address.postcode',
  'emailAddress',
  'fullName.forename',
  'fullName.prefix',
  'fullName.surname',
  'mobile',
];

class MakePaymentDetails extends Component {
  constructor(props) {
    super(props);

    this.handleAddressPopulate = this.handleAddressPopulate.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.handleFieldChange = this.handleFieldChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleResetState = this.handleResetState.bind(this);
    this.handlePaymentOptionsChange = this.handlePaymentOptionsChange.bind(this);

    this.submitContainerRef = React.createRef();

    const { data } = this.props;

    let paymentUser = data.paymentUser;
    paymentUser = initFieldsetProps(paymentUser);
    markRequiredFields(paymentUser, requiredFields);

    paymentUser.emailAddress.regex = REGEX.email;

    // Set united kingdom country dial code to default if empty string is given
    if (!paymentUser.phoneCountryDialCode.value) {
      paymentUser.phoneCountryDialCode.value = UK_DIAL_CODE;
    }

    // Set united kingdom country dial code to default if empty string is given
    if (!paymentUser.mobileCountryDialCode.value) {
      paymentUser.mobileCountryDialCode.value = UK_DIAL_CODE;
    }

    let amountObj = { amount: '' };
    amountObj = initFieldsetProps(amountObj);
    markRequiredFields(amountObj, ['amount']);

    amountObj.amount.regex = REGEX.number;

    const currencies = getCurrencies();
    const currentCurrency = getInitialCurrencyState(currencies);

    this.state = {
      ...amountObj,
      currentCurrency,
      errorMessage: null,
      isLoading: false,
      paymentUser,
      paymentOption: '0',
    };
  }

  componentDidMount() {
    window.addEventListener('pageshow', this.handleResetState);
    window.addEventListener(CUSTOM_EVENT.CURRENCY_CHANGE, this.handleCurrencyChange);

    const urlSearchParams = new URLSearchParams(window.location.search);
    const codeQueryValue = urlSearchParams.get('Code') || urlSearchParams.get('code');

    if (codeQueryValue) {
      const errorMessageKey = getVynePaymentErrorMessageKey(Number(codeQueryValue));

      this.setState(
        {
          errorMessage: this.props.labels[errorMessageKey],
        },
        () => {
          if (this.submitContainerRef.current && this.state.errorMessage) {
            window.scrollTo({ top: this.submitContainerRef.current.getBoundingClientRect().top });
          }
        }
      );
    }
  }

  componentWillUnmount() {
    window.removeEventListener('pageshow', this.handleResetState);
    window.removeEventListener(CUSTOM_EVENT.CURRENCY_CHANGE, this.handleCurrencyChange);
  }

  handleCurrencyChange = (event) => {
    const { amount, currentCurrency } = this.state;
    const userSelectedCurrency = event?.detail;

    // Now we have the user's selected currency, we need to update the
    // value via the exchange rates we are provided on Header
    const exchangeRates = getExchangeRates();

    // 1. Convert previous value to GBP
    const currentRateToGBP =
      exchangeRates.find((rate) => rate.currency.code === currentCurrency?.code).rate ?? 1;
    const parsedAmount = parseFloat(amount.value) ?? 0;
    const safeParsedAmount = Number.isNaN(parsedAmount) ? 0 : parsedAmount;
    const currentValueInGBP = safeParsedAmount * (1 / currentRateToGBP);
    // 2. Calculate new price from GBP -> Selected Currency
    const newRateFromGBP =
      exchangeRates.find((rate) => rate.currency.code === userSelectedCurrency?.code).rate ?? 1;
    const currentValueInNewCurrency = currentValueInGBP * newRateFromGBP;
    // 3. Format price for display to the user
    const roundedNewValue = currentValueInNewCurrency.toFixed(2);
    const valueToDisplay = amount.value === '' ? '' : roundedNewValue;

    this.setState((state) => ({
      currentCurrency: userSelectedCurrency,
      amount: {
        ...state.amount,
        value: `${valueToDisplay}`,
      },
    }));
  };

  handleResetState() {
    this.setState({ isLoading: false });
  }

  handlePaymentOptionsChange(paymentOption) {
    this.setState({ paymentOption });
  }

  validateForm() {
    // Check required fields for paymentUser
    const { paymentUser, amount } = this.state;
    const { labels } = this.props;

    const formIsInvalid = validateFieldset(paymentUser);

    // Validate amount
    const amountObj = { amount };
    const amountIsInvalid = validateFieldset(amountObj);

    // Build error message
    let errorMessage = null;
    if (formIsInvalid) errorMessage = labels.validationReviewFields;
    else if (amountIsInvalid) errorMessage = labels.validationReviewAmount;

    // Update state & return if form is invalid
    this.setState({
      ...amountObj,
      errorMessage,
      paymentUser,
    });

    return formIsInvalid || amountIsInvalid;
  }

  handleAddressPopulate(address) {
    const { paymentUser } = this.state;

    paymentUser.address = address;

    this.setState({ paymentUser });
  }

  handleBlur(event) {
    const newState = updateField(this.state, event.target.name, event.target.value, ({ value }) => {
      let newValue = value;

      if (typeof value === 'string') {
        newValue = value.trim();
      }

      return newValue;
    });

    this.setState(newState);
  }

  handleFieldChange(event) {
    const newState = updateField(this.state, event.target.name, event.target.value);

    this.setState(newState);
  }

  handleSubmit(event) {
    event.preventDefault();

    const formIsInvalid = this.validateForm();
    if (formIsInvalid) return;

    this.setState({
      errorMessage: null,
      isLoading: true,
    });

    const { amount, currentCurrency, paymentUser } = this.state;

    const options = {
      amount: amount.value,
      currency: currentCurrency.code,
      paymentUser: {
        address: {
          addressLine1: paymentUser.address.addressLine1.value,
          addressLine2: paymentUser.address.addressLine2.value,
          city: paymentUser.address.city.value,
          county: paymentUser.address.county.value,
          postcode: paymentUser.address.postcode.value,
        },
        emailAddress: paymentUser.emailAddress.value,
        fullName: {
          forename: paymentUser.fullName.forename.value,
          prefix: paymentUser.fullName.prefix.value,
          surname: paymentUser.fullName.surname.value,
        },
        mobile: paymentUser.mobile.value,
        mobileCountryDialCode: paymentUser.mobileCountryDialCode.value,
        phone: paymentUser.phone.value,
        phoneCountryDialCode: paymentUser.phoneCountryDialCode.value,
      },
      paymentOption: Number(this.state.paymentOption),
    };

    jsonPost(this.props.apiURL, options, (response) => {
      if (response.errorCode) {
        const errorMessageKey = getErrorMessageKey(response.errorCode);
        return this.setState({
          errorMessage:
            this.props.labels[errorMessageKey] || response.message || 'An error occurred',
          isLoading: false,
        });
      }

      window.location.href = response.redirectUri;
      return this.setState({ errorMessage: null });
    });
  }

  render() {
    const {
      data: { bankTransferPaymentsEnabled },
      labels,
    } = this.props;
    const { amount, currentCurrency, errorMessage, isLoading, paymentUser } = this.state;

    return (
      <ErrorBoundary>
        <div className="MakePayment">
          <div className="MakePayment_content">
            <div className="MakePayment_column">
              {/* Amount */}
              <div className="MakePayment_fieldset">
                <h4 className="MakePayment_fieldsetTitle">{labels.amountLabelText}</h4>
                <div className="MakePayment_group">
                  <div className="MakePayment_item MakePayment_item-1/3">
                    <div className="MakePayment_amount">
                      <div className="MakePayment_currency">{currentCurrency?.symbol}</div>
                      <Input
                        blockName="MakePayment_amountInput"
                        fieldName="amount"
                        fieldValue={amount.value}
                        handleChange={this.handleFieldChange}
                        inputType="number"
                        isDisabled={false}
                        isInvalid={amount.isInvalid}
                        isRequired={amount.isRequired}
                        modifier="ghost"
                        placeholderText={labels.placeholderTexts.amountPlaceholderText}
                        onKeyDown={blockInvalidCharactersForNumberInput}
                      />
                    </div>
                  </div>
                </div>
              </div>
              {/* Details */}
              <div className="MakePayment_fieldset">
                <h4 className="MakePayment_fieldsetTitle">{labels.detailsHeaderText}</h4>
                <MemberDetails
                  fieldNamePrefix="paymentUser"
                  fieldset={paymentUser}
                  handleBlur={this.handleBlur}
                  handleFieldChange={this.handleFieldChange}
                  handleKeyDown={preventKeyCode(191)} // Prevent user from pressing forward slash "/"
                  titleOptions={labels.titleOptions}
                  hideDateOfBirth
                  titlePlaceholderText={labels.placeholderTexts.titlePlaceholderText}
                  forenamePlaceholderText={labels.placeholderTexts.forenamePlaceholderText}
                  surnamePlaceholderText={labels.placeholderTexts.surnamePlaceholderText}
                />
              </div>
              {/* Address */}
              <div className="MakePayment_fieldset">
                <h4 className="MakePayment_fieldsetTitle">{labels.addressHeaderText}</h4>
                <MemberAddress
                  apiKey={labels.pcaPredictKey}
                  buttonText={labels.findAddressButtonText}
                  fieldNamePrefix="paymentUser"
                  fieldset={paymentUser}
                  handleAddressPopulate={this.handleAddressPopulate}
                  handleFieldChange={this.handleFieldChange}
                  postcodePlaceholderText={labels.placeholderTexts.postcodePlaceholderText}
                  addressLine1PlaceholderText={labels.placeholderTexts.addressLine1PlaceholderText}
                  addressLine2PlaceholderText={labels.placeholderTexts.addressLine2PlaceholderText}
                  cityPlaceholderText={labels.placeholderTexts.cityPlaceholderText}
                  telephonePlaceholderText={labels.placeholderTexts.telephonePlaceholderText}
                  mobilePhonePlaceholderText={labels.placeholderTexts.mobilePhonePlaceholderText}
                  emailAddressPlaceholderText={labels.placeholderTexts.emailAddressPlaceholderText}
                />
              </div>
              {/* Payment options */}
              {bankTransferPaymentsEnabled && (
                <div className="MakePayment_fieldset">
                  <h2 className="MakePayment_fieldsetTitle">{labels.paymentFieldsetTitle}</h2>
                  <fieldset className="Booking_item">
                    <PaymentOptions
                      currencySymbol={this.state.currentCurrency?.symbol}
                      handleChange={this.handlePaymentOptionsChange}
                      hasBankPaymentOption={bankTransferPaymentsEnabled}
                      price={amount.value}
                      priceText={labels.totalToPayText}
                      optionBankOptionText={labels.paymentOptionBankTransferPayInFullText}
                      optionCardOptionText={labels.paymentOptionCardPayInFullText}
                      selectedValue={this.state.paymentOption}
                    />
                  </fieldset>
                </div>
              )}
              {/* Submit */}
              <div className="MakePayment_submit" ref={this.submitContainerRef}>
                {errorMessage && (
                  <div className="MakePayment_item">
                    <ErrorMessage text={errorMessage} />
                  </div>
                )}
                <div className="MakePayment_item MakePayment_item-center">
                  <Button
                    buttonType="submit"
                    handleClick={this.handleSubmit}
                    isLoading={isLoading}
                    modifier="fill"
                    text={labels.submitButtonText}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </ErrorBoundary>
    );
  }
}

MakePaymentDetails.propTypes = propTypes;
MakePaymentDetails.defaultProps = defaultProps;

export default MakePaymentDetails;
